import { useEffect, useRef, useState } from "react";
import { AbnBusinessEntityDetail, AcnBusinessEntityDetail, ValidatedBusinessDetailsResponse, verifyBusinessNumber } from "./Api/apiBusinessNumber";
import { Validator } from "./useValidation";

export interface IBusinessValidationAttributes  {
	abn?: string;
	acn?: string;
	abnDetails?: AbnBusinessEntityDetail;
	acnDetails?: AcnBusinessEntityDetail;
}

/**
 * Validates ABN using the Validator class
 */
export function validateAbn(abn: string | null | undefined, isRequired: boolean): string[] {
	const validator = new Validator<string | null | undefined>("ABN", abn);
	
	if (isRequired) {
		validator.assertRequired("ABN is required");
	}
	
	if (abn && abn.length > 0) {
		validator.assertRule(
			value => !value || value.length === 11,
			"ABN must be 11 digits"
		);
	}
	
	return validator.getErrors();
}

/**
 * Validates ACN using the Validator class
 */
export function validateAcn(acn: string | null | undefined): string[] {
	const validator = new Validator("ACN", acn);
	
	if (acn && acn.length > 0) {
		validator.assertRule(
			value => !value || value.length === 9,
			"ACN must be 9 digits"
		);
	}
	
	return validator.getErrors();
}

/**
 * Validates business numbers
 */
function validateBusinessNumbers(
	args: AusBusinessNumberArgs,
	validateRequired: boolean = false
): AusBusinessNumberErrors | undefined {
	const abnErrors = validateAbn(args.abn, validateRequired && !!args.requiredAbn);
	const acnErrors = validateAcn(args.acn);
	
	const hasErrors = abnErrors.length > 0 || acnErrors.length > 0;
	
	if (!hasErrors) return undefined;
	
	return {
		...(abnErrors.length > 0 && { abn: abnErrors }),
		...(acnErrors.length > 0 && { acn: acnErrors })
	};
}

/**
 * Parses API error response for ABN and ACN errors
 */
function parseApiErrors(response: ValidatedBusinessDetailsResponse): AusBusinessNumberErrors | undefined {
	
	if (response.valid) return undefined;
	
	const errors: AusBusinessNumberErrors = {};
	
	if (response.errorMessage?.includes("ABN")) {
		errors.abn = [response.errorMessage];
	}
	
	if (response.errorMessage?.includes("ACN")) {
		errors.acn = [response.errorMessage];
	}
	
	return Object.keys(errors).length > 0 ? errors : undefined;
}

export type AustralianBusinessIdentifier = {
	abn?: string;
	acn?: string;
}

export type AusBusinessNumberValidationRules = {
	requiredAbn?: boolean;
	// future: requiredAcn
}

export type AusBusinessNumberArgs = AustralianBusinessIdentifier & AusBusinessNumberValidationRules & {
	skipAcnFormatValidation?: boolean;
};

export type AusBusinessNumberErrors = {
	abn?: string[],
	acn?: string[],
};

export const UseAusBusinessHelper = {
	anyErrors: function (results?: (AusBusinessNumberResult | undefined)[]) {
		if (!results?.length) return false;
		return results.some(result =>
			['abn', 'acn'].some(key => !!result?.errors?.[key as keyof typeof result.errors])
		);
	},

	anyLoading: function (results?: (AusBusinessNumberResult | undefined)[]) {
		if (!results?.length) return false;
		return results.some(result => result?.loading == true);
	},
};

type AuBusinessNumberValidationResponse = Pick<ValidatedBusinessDetailsResponse, 'abnDetails' | 'acnDetails'>;

export type AusBusinessNumberResult = AuBusinessNumberValidationResponse & {
	loading: boolean;
	errors?: AusBusinessNumberErrors;
	abn?: string;
	acn?: string;
}

/**
 * Determines whether the current request has already been fetched
 * based on previously returned ABN or ACN identifier numbers.
 */
export function previouslyFetched(
	result: AuBusinessNumberValidationResponse | undefined,
	args: AusBusinessNumberArgs
): boolean {
	if (!result) return false;

	const prevAbn = result.abnDetails?.identifierNumber;
	const prevAcn = result.acnDetails?.identifierNumber;

	const abnMatch = !!prevAbn && !!args.abn && prevAbn === args.abn;
	const acnMatch = !!prevAcn && !!args.acn && prevAcn === args.acn;

	return abnMatch || acnMatch;
}

export function hasValue(identifier?: AustralianBusinessIdentifier): boolean {
	if (!identifier) return false;
	return !!(
		(identifier.abn && identifier.abn !== '') ||
		(identifier.acn && identifier.acn !== '')
	);
}


export function useAusBusinessNumber(args: AusBusinessNumberArgs): AusBusinessNumberResult {
	const [loading, setLoading] = useState(false);
	const [result, setResult] = useState<AuBusinessNumberValidationResponse | undefined>(undefined);
	const [errors, setErrors] = useState<AusBusinessNumberErrors | undefined>(undefined);

	const prevArgs = useRef<AusBusinessNumberArgs>();
	const wasPreviouslyFetched = previouslyFetched(result, args);

	useEffect(() => {
		const validate = async () => {
			try {
				setLoading(true);

				const newArgsHasValue = hasValue(args);
				const prevArgsHasValue = hasValue(prevArgs.current);

				// 1. No new or old user input
				if (!newArgsHasValue && !prevArgsHasValue) return;

				// 2. User cleared inputs when it was previously filled
				if (!newArgsHasValue && prevArgsHasValue) {
					const requiredErrors = validateBusinessNumbers(args, true);

					if (requiredErrors) {
						setErrors(requiredErrors);
						setResult(undefined);
						return;
					} else {
						setResult(undefined);
						setErrors(undefined);
						return;
					}
				}

				// 3. Previously fetched - do nothing
				if (wasPreviouslyFetched) return;

				// 4. Preparing validation for new user input
				setLoading(true);

				// 4.a Local format validation
				const formatErrors = validateBusinessNumbers(args, false);
				if (formatErrors) {
					setErrors(formatErrors);
					setResult(undefined);
					return;
				}

				// 4.b API validation
				const response = await verifyBusinessNumber(args.abn, args.acn, args.skipAcnFormatValidation);
				const apiErrors = parseApiErrors(response);
				
				if (apiErrors) {
					setErrors(apiErrors);
					setResult(undefined);
					return;
				}

				// 4.c Validation on response business numbers
				const finalAbn = response.abnDetails?.identifierNumber;
				const finalAcn = response.acnDetails?.identifierNumber;
				const argsToValidate: AusBusinessNumberArgs = { 
					...args, 
					abn: finalAbn, 
					acn: finalAcn 
				};
				
				const finalErrors = validateBusinessNumbers(argsToValidate, true);
				
				if (finalErrors) {
					setErrors(finalErrors);
					setResult(undefined);
					return;
				}

				// 5. Passed all validations without error
				setResult(response);
				setErrors(undefined);
				
			} finally {
				setLoading(false);
				prevArgs.current = args;
			}
		};

		validate();
	}, [args, wasPreviouslyFetched]);

	const useResponseData = !loading && !errors;

	return {
		loading,
		errors: !loading ? errors : undefined,
		abnDetails: useResponseData ? result?.abnDetails : undefined,
		acnDetails: useResponseData ? result?.acnDetails : undefined,
		abn: useResponseData ? result?.abnDetails?.identifierNumber : undefined,
		acn: useResponseData ? result?.acnDetails?.identifierNumber : undefined
	};
}